import { FormikErrors, FormikValues } from "formik";
import { Schema } from "yup";
import { AppDispatch, RootState } from "../app/store";
import { FormikProps } from "formik";
import { ReactNode } from "react";
import { NavigateFunction } from "react-router-dom";
import { ButtonProps } from "@allaria-tech/matter/Button";
import { LogoProduct } from "@allaria-tech/matter/Logo";
import type { Timestamp } from "firebase/firestore";
import { TextFieldProps } from "@allaria-tech/matter/TextField";

/**
 * Selection mode of a `select` field. Only the mode's "multiple" prefix is
 * read (see `TemplateInputSelector`), which is what matter's `InputSelect`
 * exposes as its `multiple` flag.
 */
export type SelectType =
  | "single"
  | "multiple"
  | "single-radio"
  | "multiple-checkbox"
  | "multiple-sections-checkbox"
  | "single-radio-date-picker"
  | "single-radio-date-picker-day";

export type OnboardingType =
  | "PHYSICAL"
  | "JURIDICAL"
  | "SIGNATORY"
  | "SECURITIES"
  | "COOWNER"
  | "ADDITIONAL_COOWNER"
  | "DIGITAL"
  | null;
export type CompanyType =
  | "ALLARIA"
  | "ALLARIAMAS"
  | "AGROFINANZAS"
  | "ALLARIA_SECURITY"
  | "ALLARIADIGITAL"
  | "ALLARIAFONDOS";

export type PersonType = "PHYSICAL" | "JURIDICAL";

/**
 * Configuration data for a company participating in the onboarding flow.
 */
export type CompanyData = {
  formTitle: string;
  logo: string;
  logoClassname?: string;
  /** matter `Logo` brand shown in the header for this company. */
  logoKey?: LogoProduct;
  favicon: string;
  companyName: CompanyType;
  phone: string;
  mail: string;
  termsAndConditions: {
    required: boolean;
    name: string;
    label: JSX.Element;
    /** Links referenced within the terms and conditions label. */
    links: { id: string; name: string; url: string }[];
  }[];
  partnerships?: Partnership[];
};

export interface SuccessRedirect {
  buttonText: string;
  /** Message shown above the redirect button. Receives the countdown seconds so it can embed the remaining time. */
  message: (secondsLeft: number) => string;
  /** If provided, the page auto-redirects after this many milliseconds. */
  autoRedirectMs?: number;
  /** Allowed redirect URL domains. The `redirect_url` query param must match one of these. */
  allowedDomains: string[];
  /**
   * Where to go when the link carried no `redirect_url`. Without one the
   * redirect UI is hidden entirely, which is right for partnerships that only
   * ever arrive with the param and wrong for ones whose screens are designed
   * around the button. Validated against `allowedDomains` like any other URL.
   */
  defaultUrl?: string;
}

export interface Partnership {
  /** Unique key for the partnership used to identify current partner. */
  key: string;
  name: string;
  logo: string;
  logoClassname: string;
  onboardingTypes: OnboardingType[];
  /** For dynamic templates with custom steps, follows the order of appearance of this array */
  onboardingSteps?: Partial<
    // Personalization is currently only available for physical onboarding
    Record<NonNullable<OnboardingType>, PhysicalStep[]>
  >;
  /** When true, the partnership logo replaces the company logo instead of appearing alongside it. */
  replacesCompanyLogo?: boolean;
  /** When set, overrides the active template's theme for this partnership. */
  theme?: OnboardingTheme;
  /**
   * When set, the success page shows a message + CTA button that redirects to the partnership.
   * The target URL is read from the `redirect_url` query param — if it's missing, the redirect UI is not rendered.
   */
  successRedirect?: SuccessRedirect;
  /** Merchant-specific overrides scoped to this partnership. Matched against the merchant token in the `ref` query param. */
  merchants?: Merchant[];
  /** Allaria Fondos: always runs the full onboarding, even if the company already has an Allaria account. */
  skipActiveAccountSummary?: boolean;
}

export interface Merchant {
  /** Unique key for the merchant within its partnership. Matched case-insensitively against the merchant token in the `ref` query param. */
  key: string;
  /** When set, overrides the partnership theme for this merchant. */
  theme?: OnboardingTheme;
  /** When set, the merchant logo is rendered alongside the partnership logo (it never replaces it). */
  logo?: string;
  /** SCSS module classname applied to the merchant logo when rendered. */
  logoClassname?: string;
}

export type ValidationType =
  | "FACE_LIVENESS_DETECTION"
  | "RENAPER"
  | "FACIAL_RECOGNITION"
  | "DNI_SCAN";

export type DocumentRepresentativeType = "DNI_FRONT" | "DNI_BACK" | "SELFIE";

export type DocumentIndividualType =
  | DocumentRepresentativeType
  | "WORTH_STATEMENT";

export type DocumentType =
  | "DNI_FRONT"
  | "DNI_BACK"
  | "SELFIE"
  | "WORTH_STATEMENT"
  | "PROOF_OF_ADDRESS"
  | "PASSPORT_FIRST_PAGE"
  | "PASSPORT_SECOND_PAGE";

export type DocumentBusinessType =
  | "BALANCE" // 3 Balance certificado por CPCE o doc. patrimonial
  | "WORTH_STATEMENT" // 7 Documentación patrimonial
  | "SHARES_REGISTER" // 4 Registro de accionistas
  | "OTHERS" // 13 Otros
  | "STATUTE" // 1 Estatuto o contrato social escaneado
  | "POA" // 2 Poder de los firmantes
  | "AUTHORITIES" // 5 Acta de designación de autoridades
  | "MEMBERS_NOTE" // 10 Nota de miembros
  | "SHAREHOLDERS_NOTE" // 12 Nota de accionistas y beneficiarios finales
  | "TRUST_NOTE" // 11 Nota de fidunciantes, fiduciarios y beneficiarios
  | "PARTNERS_NOTE" // 8 Nota de socios protectores y beneficiarios
  | "PARTNERS_LIST" // 9 Nómina de socios
  | "EXPENSE_LIQUIDATION"
  | "UIF_INSCRIPTION_CERTIFICATE" // 6 "Constancia de inscripción ante UIF
  | "HORIZONTAL_PROPERTY_REGULATIONS"
  | "TRUST_CONSTITUTION_CONTRACT"
  | "CO_OWNER_LIST";

export interface Steps {
  step: number | null;
  subStep: number | null;
}

export interface PreconfirmationField {
  name: string;
  label: string;
  type?: InputType;
  options?: Option[];
  isConditional?: boolean;
  formatInPreConfirmation?: (
    value: string,
    formValues?: FormikValues | null,
  ) => string;
  values?: FormikValues | null;
  valueGetter?: (values: FormikValues) => string;
  canUploadMultipleFiles?: boolean;
}

export interface PreconfirmationStep {
  name: string;
  columns: number;
  fields?: PreconfirmationField[];
  isDocumentStep?: boolean;
  fieldsWithSeparator?: PreconfirmationField[][];
  editStep?: {
    step: number;
    substep?: number;
  };
  hideStep?: boolean;
}

export interface PreconfirmationCheckbox {
  name: string;
  label: ReactNode;
  initialValue: boolean;
  required: boolean;
  validation: Schema;
  links?: {
    id: string;
    name: string;
    url: string;
  }[];
}

export interface Preconfirmation {
  headerContent?: ReactNode;
  steps: PreconfirmationStep[];
  checkboxes?: PreconfirmationCheckbox[];
  sendTermsAndConditions?: (
    state: RootState,
    values: FormikValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  tabs?: {
    label: string;
    steps: PreconfirmationStep[];
  }[];
  defaultSelectedKey?: (props: {
    dispatch: AppDispatch;
    state: RootState;
  }) => string | undefined;
  submitButtonText?: (props: {
    state: RootState;
    formValues: FormikValues | null;
  }) => string;
  disabledKeys?: (props: {
    dispatch: AppDispatch;
    state: RootState;
  }) => string[];
  onChangeTab?: (props: {
    key: string;
    dispatch: AppDispatch;
    state: RootState;
  }) => void;
  onSubmit?: (props: {
    values: FormikValues;
    state: RootState;
    dispatch: AppDispatch;
    navigate: NavigateFunction;
  }) => Promise<void>;
}

export type OnboardingTheme = "ALLARIA" | "DIGITAL" | "SAV3";

export type Onboarding = (
  draftUUID: DraftUUID,
  dispatch: AppDispatch,
  formValues?: FormikValues | null,
  navigate?: NavigateFunction,
) => Promise<Template>;

export type TemplateOnLoad = (params: {
  draftUUID?: string;
  dispatch: AppDispatch;
  formik: FormikProps<FormikValues>;
  state: RootState;
}) => Promise<void>;

export interface Template {
  formTitle: string;
  logo: string;
  steps: Array<Step> | null;
  remoteSteps?: {
    link: string;
    step: Step;
  }[];
  icon?: string;
  onboardingType: OnboardingType;
  preconfirmation:
    | null
    | (({ values }: { values?: FormikValues | null }) => Preconfirmation);
  favicon: string;
  hideLogo?: boolean;
  hideSupportButton?: boolean;
  buttonsInsideContainer?: boolean;
  customButton?: { text: string; onClick: () => void };
  theme?: OnboardingTheme | null;
  onLoad?: TemplateOnLoad;
  dotsDisabled?: boolean;
}

export interface Step extends BasicStep, ComponentStep {
  subSteps?: Array<BasicStep | ComponentStep>;
}

// Label of step
export type PhysicalStep =
  | "basicInformation"
  | "token"
  | "emailValidation"
  | "identity"
  | "personalInformation"
  | "employmentProfile"
  | "investmentProfile";

interface StepBase {
  /** Position in the onboarding flow. */
  step: number | string;
  /** name/key for the step, to show inside form and stepper . */
  name?: string;
  /** Overrides the form container title on mobile only. Falls back to `titleOfStep`/`name`. */
  mobileName?: string;
  /** Form container title for this step. Falls back to `name`. */
  titleOfStep?: string;
  /** Color of the form container title, e.g. a theme token's `var()`. */
  titleColor?: string;
  /** Dots beside the form container title: `current` of `total`, 1-based. */
  dotStep?: { current: number; total: number };
  /** Supporting text rendered under the form container title. */
  subtitle?: string;
  /** To identify step for custom templates or remote steps */
  label?: PhysicalStep | string;
  /**
   * Disables back navigation from this step. Shows a lock icon in the previous stepper item,
   * and prevents users from clicking it in the stepper to navigate back.
   */
  disableBackNavigation?:
    | boolean
    | ((
        values: FormikValues,
        nextStepStatus: "completed" | "disabled",
      ) => boolean);
  /** When `true`, hides the top stepper progress bar */
  hideStepper?: boolean;
  hideStepperOnMobile?: boolean;
  hideHeaderOnMobile?: boolean;
  hideFormHeader?: boolean | ((state: RootState) => boolean);
  hideFormHeaderOnMobile?: boolean;
  centeredLogo?: boolean;
  /** When `true`, excludes this step from being rendered as a stepper item even though it still participates in the flow */
  hideFromStepper?: boolean;
  /** Overrides the number shown in the stepper bubble without affecting internal step logic. */
  displayStep?: number;
  /** When `true`, hides the entire layout button row (back + continue). */
  hideLayoutButtons?: boolean;
  /** When `true`, hides the back button. */
  disableButtonBack?: boolean;
  /** Replaces the back button with this one, shown even if `disableButtonBack`. */
  secondaryButton?: { text: string; onClick: () => void };
  /** URL or asset path for an icon associated with this step. */
  icon?: string;
  /** Configuration for an extra component rendered alongside the step (above or below), with a `show` condition and a `position`. */
  supplementaryComponentInfo?: SupplementaryComponentInfoProps;
  /**
   * When it returns true, the step is treated as not-applicable for the current form values:
   * it is skipped when computing the resume point (`lastCompletedStepData`). Mirrors the imperative
   * skip performed in `onContinue` via `dispatch(onUpdateStep(...))` so both stay in sync.
   */
  skipWhen?: (values: FormikValues, state: RootState) => boolean;
}

export interface BasicStep extends StepBase {
  fieldsSections?: FieldsSection[];
  fields?: Field[];
  onContinue?: OnContinue;
  onBack?: OnBack;
}

export type ComponentProps = {
  dispatch?: AppDispatch;
  state?: RootState;
  formik?: FormikProps<FormikValues>;
};

export type Component = (
  dispatch: AppDispatch,
  state?: RootState,
  formik?: FormikProps<FormikValues>,
) => JSX.Element;

type ButtonHandler = (
  values: FormikValues,
  dispatch?: AppDispatch,
  state?: RootState,
  setFieldValue?: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Promise<any>;

export type OnContinue = ButtonHandler;
export type OnBack = ButtonHandler;

export type SetPrimaryFormValues = (
  values: React.SetStateAction<FormikValues>,
  shouldValidate?: boolean,
) => Promise<void | FormikErrors<FormikValues>>;

export interface SecondaryFormMobileSection {
  title: string;
  fields: string[];
}

export interface SecondaryForm {
  title?: (activeIndex?: number) => string;
  mobileTitle?: string;
  /**
   * Replaces `mobileTitle` when the form opens on an entity that already has
   * data stored — the list's edit action — instead of on an empty one.
   */
  mobileEditTitle?: string;
  mobileSections?: (
    activeTab: string,
  ) => SecondaryFormMobileSection[] | undefined;
  collectionName: string;
  inline?: boolean;
  fields: Field[];
  onSave: (
    values: FormikValues,
    dispatch?: AppDispatch,
    state?: RootState,
    setPrimaryFormValues?: SetPrimaryFormValues,
    formik?: FormikProps<FormikValues>,
  ) => void;
  primaryForm?: FormikProps<FormikValues>;
  tabs?: {
    options: {
      id: string;
      label: string;
      leadingIcon?: ReactNode;
    }[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    active: any;
    onChange: (
      tab: string,
      setActiveTab: React.Dispatch<React.SetStateAction<string>>,
      dispatch?: AppDispatch,
      state?: RootState,
      values?: FormikValues,
    ) => void;
    disabled: (state: RootState) => boolean;
  };
  alert?: {
    id: string;
    title: string;
    message: string;
    primaryButton: {
      text: string;
      action: (tab?: string, dispatch?: AppDispatch, state?: RootState) => void;
    };
  };
  /**
   * Removes the entity being edited. Rendered as the delete action of the form
   * header, next to the save button, and confirmed through `alert`.
   */
  deleteEntity?: {
    /**
     * Confirmation asked before removing, skipped when the entity has no data
     * stored yet. `title` receives that stored entity so the copy can name it.
     */
    alert?: {
      title: (entity: FormikValues) => ReactNode;
      message?: string;
    };
    /** Entities that cannot be removed, e.g. the account holder */
    hidden?: (state: RootState) => boolean;
    disabled?: (state: RootState) => boolean;
    onClick: (
      dispatch: AppDispatch,
      state: RootState,
      setPrimaryFormValues?: SetPrimaryFormValues,
    ) => void;
  };
  showErrorsOnOpen?: (entity: FormikValues) => boolean;
  onSuccessCondition?: (state?: RootState) => boolean;
  onSuccessContent?: {
    title: string;
    subtitle?: string;
    message?: string;
  };
}

export interface ComponentStep extends StepBase {
  component?: Component;
  insideForm?: boolean;
  secondaryForm?: SecondaryForm;
  stepModals?: ReactNode;
}

export interface SupplementaryComponentInfoProps {
  component: () => ReactNode;
  show: boolean | ((state: RootState) => boolean);
  position: "top" | "bottom";
}

export type InputType =
  | "text"
  | "email"
  | "select"
  | "combobox"
  | "radio"
  | "phone"
  | "file"
  | "multiple-radio"
  | "textarea"
  | "address"
  | "number"
  | "dni"
  | "cuit"
  | "label"
  | "date-picker"
  | "checkbox"
  | "date"
  | "button"
  | "multiple-checkbox"
  | "money"
  | "alert"
  | "segmented";

export interface Field {
  initialValue: string | boolean;
  type: InputType;
  dynamicType?: (
    formik: FormikProps<FormikValues>,
    state?: RootState,
  ) => InputType;
  name: string;
  label: string;
  description?: string;
  conditionalValidation?: (state?: RootState) => Schema;
  validation?: Schema;
  options?: Option[] | SegmentedInputOption[];
  /**
   * If the field is conditional, it will only be shown if the conditional field has the specified value
   */
  conditional?: Conditional | Conditional[];
  allConditionalsTrue?: boolean;
  conditionalBool?: (
    values: FormikValues,
    dispatch?: AppDispatch,
    state?: RootState,
  ) => boolean;
  /**
   * If the field is conditionalFile, it will only be shown if the conditional field has the specified
   * value and the file is uploaded
   * */
  conditionalFile?: ConditionalFile;
  conditionalTooltip?: ConditionalTooltip | ConditionalTooltip[];
  placeholder?: string;
  selectType?: SelectType;
  validTypes?: string[];
  canUploadMultipleFiles?: boolean;
  infoTextPosition?: "top" | "bottom";
  infoText?: string | ((value: string | undefined) => string | undefined);
  /** `info` shows the info text with an info icon. Defaults to `hint`. */
  infoTextType?: "hint" | "info";
  tooltip?: Tooltip;
  maxSize?: number;
  confirmSelection?: boolean;
  searchable?: boolean;
  columns?: number;
  startsRow?: boolean;   // flag that forces a field to open a new row
  disabled?: boolean | ((state: RootState, values: FormikValues) => boolean);
  isNumberPercentage?: boolean;
  minDatePickerDate?: Date;
  maxDatePickerDate?: Date;
  preconfirmationLabel?: string;
  /* Object formik value path */
  autoSelect?: string;
  country?: string;
  formatInPreConfirmation?: (
    value: string,
    formValues?: FormikValues | null,
  ) => string;
  onChange?: OnChange;
  onBlur?: OnChange;
  showOptionalLabel?: boolean;
  groupReorder?: string;
  autoComplete?: "on" | "off";
  dontScrollTo?: boolean;
  anchor?: {
    text: string;
    onClick?: () => void;
  };
  maxLength?: number;
  inputMode?: TextFieldProps["inputMode"];
  valueGetter?: (values: FormikValues) => string;
  /**
   * If the field is of type "select" and the options need to change dynamically based on other form values,
   * this function will be called to generate the options.
   * The function receives the current Formik props and should return an array of options.
   */
  dynamicOptions?: (
    formik: FormikProps<FormikValues>,
    state?: RootState,
  ) => Option[];
  disabledByUrlParams?: string | string[];
  text?: ReactNode;
  buttonProps?: {
    text: string;
    variant?: ButtonProps["variant"];
    type?: ButtonProps["type"];
    startContent?: ReactNode;
    disabled?: (values: FormikValues, state: RootState) => boolean;
    onClick?: (
      values: FormikValues,
      dispatch?: AppDispatch,
      state?: RootState,
    ) => void;
  };
  /* Disable paste in the input */
  disablePaste?: boolean;
  /* Hide input's label (if any) */
  hideLabel?: boolean;
  /** Conditionally display the field based on a step condition */
  hideField?: boolean;
  className?: string; // For styling the input container
  /* For type 'alert' */
  alertContent?: () => ReactNode;
}

export interface FieldsSection {
  name: string;
  fields: Field[];
  conditional?: Conditional | Conditional[];
  allConditionalsTrue?: boolean;
}

export type OnChange = (
  values: FormikValues,
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
  setFieldError?: (field: string, message: string | undefined) => void,
  setFieldTouched?: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void,
  element?: React.ChangeEvent<HTMLInputElement>,
  state?: RootState,
  dispatch?: AppDispatch,
) => void;

export type OnValidation = (state?: RootState) => Schema;

export interface Tooltip {
  label?: string | ReactNode;
  modal?: string;
  icon?: string;
  content?: string;
  width?: string | number;
  /**
   * For cases where the tooltip is needed to be rendered outside the input.
   * For example, when the input is disabled and the tooltip needs to be enabled.
   */
  external?: boolean;
}

export interface ButtonData {
  text: string;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
  type?: "submit" | "button";
  show?: boolean;
  /** Only for the tertiary button: draws it as a secondary one. */
  variant?: "secondary";
}

export interface Option {
  value: string;
  label: string;
}

export interface SegmentedInputOption {
  value: string;
  label: string;
  checked: boolean;
}

export interface Conditional {
  name: string;
  value: string | (string | undefined)[] | File | boolean;
}

export interface ConditionalFile {
  name: string;
}

export interface ConditionalTooltip extends Conditional {
  tooltip: Tooltip;
}

export interface StepperProps {
  steps: Steps;
  setSteps: React.Dispatch<React.SetStateAction<Steps>>;
  template: Template | null;
}

export type Email = string;
export type DraftUUID = string;
export type GenderInitials = "M" | "F";
export type Gender = "MALE" | "FEMALE" | "OTHER";
export type CivilStatus = "SINGLE" | "MARRIED" | "DIVORCED" | "WIDOWED";
export type IdentityType = "DNI";
export type IdentityVersion = "A" | "B" | "C" | "D" | "E";
export type Pep = "DIRECT" | "FAMILY";
export type EmploymentYears =
  | "Menos de un año"
  | "1 año"
  | "2 años"
  | "3 años"
  | "4 años"
  | "5 años"
  | "6 años"
  | "7 años"
  | "8 años"
  | "9 años"
  | "Más de 10 años";
export type EmploymentStatus =
  | "DEPENDENCY_RELATIONSHIP"
  | "STUDENT"
  | "INDEPENDENT"
  | "MONOTAX"
  | "AUTONOMOUS"
  | "RETIRED"
  | "UNEMPLOYED";

export type CorporateType =
  | "SA - Sociedad Anónima"
  | "SRL - Sociedad de Responsabilidad Limitada"
  | "Asociación / Cámara / Cooperativa / Fundación / Mutual"
  | "Fideicomiso"
  | "SAS - Sociedad por Acciones Simplificada"
  | "SGR - Sociedad de Garantía Recíproca"
  | "Organismo y/o Entidad Gubernamental"
  | "Consorcio"
  | "Otro";

export type CorporateTypeTranslated =
  | "saCorporation"
  | "llcLimitedLiabilityCompany"
  | "associationFoundationCooperativeChamber"
  | "trust"
  | "simplifiedJointStockCompany"
  | "reciprocalGuaranteeCompany"
  | "consortium"
  | "governmentAgencyOrEntity"
  | "other";

export type DniImageType = "frontDNI" | "backDNI";

export interface Entity {
  label: string;
  value: string;
  placeholder?: string;
}

export interface FirebaseDniData {
  scannedQR: boolean;
  dniScanValidation: string;
  completedRemote: boolean;
  fallbackUploaded?: boolean;
  scanAttemptUsed?: boolean;
  /** Vencimiento que consume la function cleanupRenaper (drafts abandonados). */
  expiresAt?: Timestamp;
}

export interface DraftBase {
  cuit: string;
  referrer?: string;
  juridicalPersonCuit?: string;
}

export interface AuthToken {
  email: string;
  signatureRequestId: number;
  token: string;
}
export interface AuthSignatoryToken extends AuthToken {
  externalCode: string;
}

export interface RegenToken {
  email: string;
  signatureRequestId: number;
  companyType: CompanyType;
  partnerCompanyType?: PartnerCompanyType;
  oldAuth?: boolean;
  es?: boolean;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  country?: string;
  number?: number;
  floor?: number;
  apartment?: string;
  zipCode?: string;
  municipality?: string;
  fullAddress?: string;
}

export interface IdentityDocument {
  type: IdentityType;
  idNumber: string;
  procedureId: string;
  version: IdentityVersion;
  creationDate: string;
  expirationDate: string;
}

export interface RepresentativeInformation {
  email?: Email;
  cuit: string;
  name: string;
  lastName: string;
  gender: Gender;
  civilStatus: CivilStatus;
  birthDate: string;
  citizenship: string;
  birthCountry: string;
  telephone: string;
  pep?: Pep;
  addresses: Address[];
  identityDocument: IdentityDocument;
  pepDetails?: string;
  activities?: string[];
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface Representative {
  "@type": string;
  referrer: string;
  representativeInformation: RepresentativeInformation;
}

export type QualifiedInvestorType = "NONE" | "NET_WORTH";

export interface InvestmentProfile {
  choices: string[];
}

export interface PersonalInformation {
  email?: string;
  cuit: string;
  name: string;
  lastName: string;
  gender: Gender;
  civilStatus: string;
  birthDate: string;
  citizenship: string;
  birthCountry: string;
  telephone: string;
  addresses: Address[];
  identityDocument: IdentityDocument;
  pep: string;
  pepDetails?: string;
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface Shareholder {
  taxId: string;
  taxType: "CUIT" | "NIF";
  ownership: number | string;
  foreignTaxDetailBool?: "NO" | "SI";
  foreignTaxDetail: ForeignTaxDetailWithAddress;
}

export interface PhysicalShareholder extends Shareholder {
  personType: "PHYSICAL";
  name: string;
  lastName: string;
  citizenship: string;
  birthDate: string;
  civilStatus: CivilStatus | null;
  address: Address;
  activities: string[];
  pep?: Pep;
  pepDetails?: string;
  spouseName?: string;
  spouseLastName?: string;
  spouseCuil?: string;
}

export interface PhysicalShareholderWithSpouseObject extends PhysicalShareholder {
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface PhysicalShareholderWithSpouseObject extends Omit<
  PhysicalShareholder,
  "spouseName" | "spouseLastName" | "spouseCuil"
> {
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface PhysicalShareholderFormik extends PhysicalShareholder {
  address: AddressWithFullAddress;
}

interface AddressWithFullAddress extends Address {
  fullAddress: string;
  stateSelect?: string;
  stateText?: string;
}

export interface JuridicalShareholder extends Shareholder {
  personType: "JURIDICAL";
  legalName: string;
}

export type ShareholderData = PhysicalShareholder | JuridicalShareholder | null;

export interface ForeignTaxDetail {
  idNumber: string;
  country: string;
}

export interface ForeignTaxDetailWithAddress {
  idNumber: string;
  address: Address;
}

export interface BankAccount {
  cxu: string;
}

export interface EmploymentProfile {
  activities: string[];
  years: EmploymentYears;
  employmentStatus: EmploymentStatus;
  bankAccounts?: BankAccount[];
  foreignTaxDetails?: ForeignTaxDetail;
}

export interface BusinessInformation {
  cuit: string;
  legalName: string;
  corporateType: CorporateType;
  activities: string[];
  activitiesDetails: string;
  registrationNumber: string;
  registrationAuthority: string;
  registrationAuthorityDetails: string;
  registrationCountry: string;
  registrationProvince: string;
  registrationDate: string;
  isPublicEntity: boolean;
  hasJointSignature: boolean;
  isInEconomicGroup: boolean;
  isComplexStructure: boolean;
  addresses: Address[];
  bankAccounts?: BankAccount[];
  foreignTaxDetail?: ForeignTaxDetailWithAddress;
}

export interface SignatoryData {
  email: string;
  name: string;
  lastName: string;
  idNumber?: string;
  confirmEmail?: string;
}

export type Signatory = SignatoryData | null;

export type CoownerData = SignatoryData;
export type Coowner = SignatoryData | null;

export interface StepData {
  step: number | string | null;
  // Last numeric value of `step`, kept when `step` is swapped for "information-step"
  lastNumericStep: number | null;
  subStep: number | null;
  disabledSubStep: number | null;
  completedSteps: number[] | null;
  reachedSteps: number[] | null;
  lastCompletedSubStep: number;
  numericStepList: number[] | null;
  resumeStep: number | string | null; // For ACTIVE-ACCOUNT info step
}

export type StepEndpoint =
  | StepOnePF
  | StepTwoPF
  | StepThreePF
  | StepFourPF
  | StepFivePF
  | StepSixPF
  | StepOnePJ
  | StepTwoPJ
  | StepThreePJ
  | StepFourPJ
  | StepFivePJ
  | StepSixPJ
  | StepSevenPJ
  | StepOneSecurities
  | StepTwoSecurities
  | StepThreeSecurities
  | StepFourSecurities
  | StepFiveSecurities
  | StepSixSecurities
  | StepSevenSecurities;

export type StepEndpointSecurities =
  | StepOneSecurities
  | StepTwoSecurities
  | StepThreeSecurities
  | StepFourSecurities
  | StepFiveSecurities
  | StepSixSecurities
  | StepSevenSecurities;

//----This step's types is only for BACK
//Steps of Physical Person
export interface StepOnePF {
  "@type": string;
  personalInformation: PersonalInformation;
  referrer?: string;
  accountIdToLink: string | null;
}

export interface StepTwoPF {
  "@type": string;
  employmentProfile: EmploymentProfile;
}
export interface StepThreePF {
  "@type": string;
  coOwners: Coowner[];
}

export interface StepFourPF {
  "@type": string;
  investmentProfile: InvestmentProfile;
  qualifiedInvestorType?: QualifiedInvestorType;
}

export interface StepFivePF {
  "@type": string;
  acceptedTermsAndConditions: boolean;
  acceptedOptionalTermsAndConditions: boolean;
  wantsAllariaAccount?: boolean;
}

export interface CoownerSteps {
  stepOne: StepOnePF;
  stepTwo: StepTwoPF;
  stepFour: StepFourPF;
  stepFive: StepFivePF;
}

export interface StepSixPF {
  "@type": string;
  coOwnerSteps: CoownerSteps[];
}

// PITBULL

export type PitbullActivityPhysical = { name: string };

export type PitbullActivityJuridical = {
  activity: { name: string };
  externalCode: string;
};
export type PitbullActivities =
  | PitbullActivityPhysical[]
  | PitbullActivityJuridical[];

export type PitbullCountry = {
  citizenship: string;
  name: string;
  isoAlpha2: string;
  isoAlpha3: string;
};

export type PitbullCountries = PitbullCountry[];

export interface MidgardChoice {
  externalCode: string;
  option: string;
}

export interface MidgardQuestion {
  externalCode: string;
  text: string;
  companies: string[];
  choices: MidgardChoice[];
}
export type MidgardQuestions = MidgardQuestion[];

export interface PitbullProvince {
  name: string;
  isoAlpha2: string;
  countryIsoAlpha2: string;
}
export type PitbullProvinces = PitbullProvince[];

// Steps of Juridical Person
// TODO: Define the steps for Juridical Person
export type StepOnePJ = Representative;

export interface StepTwoPJ {
  "@type": string;
  businessInformation: BusinessInformation;
}

export interface StepThreePJ {
  "@type": string;
  additionalSignatories: Signatory[];
}

export interface StepFourPJ {
  "@type": string;
  shareholders: Array<PhysicalShareholder | JuridicalShareholder>;
}

export interface StepFivePJ {
  "@type": string;
  investmentProfile: InvestmentProfile;
  qualifiedInvestorType?: QualifiedInvestorType;
}
export interface StepSevenPJ {
  "@type": string;
  representatives: StepOnePJ[];
}

export interface StepSixPJ {
  "@type": string;
  acceptedTermsAndConditions: boolean;
  acceptedOptionalTermsAndConditions: boolean;
  wantsAllariaAccount?: boolean;
}

export interface StepSevenPJ {
  "@type": string;
  representatives: Representative[];
}

export interface DocumentMetadata {
  type: string;
  addedBy: string;
  addedAt: string;
  originalFileName: string;
  contentType: string;
  publicUrl: string;
  uuid: string;
}

export type ErrorDetail = { field: string; message: string };

export interface ValidationErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: ErrorDetail;
  path: string;
}

export interface ErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  code: string;
  path: string;
}

export interface ConditionalBool {
  boolField: string | undefined;
  conditionalField: string;
  dontScrollTo: boolean;
  inputType: InputType;
}

export type StepEndpointResponse =
  | StepOnePF
  | StepTwoPF
  | StepThreePF
  | StepFourPF
  | StepFivePF
  | StepSixPF
  | StepOnePJ
  | StepTwoPJ
  | StepThreePJ
  | StepFourPJ
  | StepFivePJ
  | StepSixPJ
  | StepSevenPJ;

export interface ValidationAttempt {
  attemptedAt: string;
  email: string;
  status: "OK" | "FAILED" | "CHECK" | string;
}

export interface GetDraftResponse {
  companyType: CompanyType;
  email: string;
  externalCode: string;
  personType: PersonType;
  steps: StepEndpointResponse[];
  cuit?: string;
  referrer?: string | null;
  juridicalPersonCuit?: string | null;
  dniScanAttempts?: ValidationAttempt[];
  faceLivenessAttempts?: ValidationAttempt[];
  renaperAttempts?: ValidationAttempt[];
}

export interface UploadedFile extends File {
  __alreadyUploaded?: boolean;
}

export interface SerializableFile {
  name: string;
  type: string;
  lastModified: number;
  size: number;
  blob: ArrayBuffer;
}

export interface SecuritiesOptions {
  gender: "MALE" | "FEMALE" | "OTHER";
  educationLevel:
    | "NONE"
    | "PRIMARY"
    | "SECONDARY"
    | "TERTIARY"
    | "UNIVERSITY"
    | "POSTGRADUATE"
    | "MASTER"
    | "DOCTORATE";
  civilStatus: CivilStatus | "CONCUBINAGE";
  documentType: "DNI" | "PASSPORT";
  taxIdType: "CUIT" | "CUIL" | "SSN" | "TAX_ID";
  investmentKnowlageLevel: "LIMITED" | "MODERATED" | "EXTENSIVE" | "NONE";
  investmentTool:
    | "ETF"
    | "ANNUITIES_VARIABLE"
    | "ANNUITIES_FIXED"
    | "UNITARY_INVESTMENT_TRUSTS"
    | "EXCHANGE_TRADED_FUNDS"
    | "FIXED_INCOME"
    | "INSURANCE"
    | "MUTUAL_FUNDS"
    | "COMMODITIES_FUTURES"
    | "EQUITIES"
    | "OPTIONS"
    | "PRECIOUS_METALS"
    | "REAL_ESTATE"
    | "OTHERS";
  investmentToolsKeys:
    | "annuitiesFixed"
    | "annuitiesVariable"
    | "etfs"
    | "uits"
    | "fixedIncome"
    | "insurance"
    | "mutualFunds"
    | "commoditiesAndFutures"
    | "equities"
    | "options"
    | "preciousMetals"
    | "realEstate"
    | "others";
  employmentStatus:
    | "EMPLOYEE"
    | "INDEPENDENT"
    | "RETIRED"
    | "UNEMPLOYED"
    | "STUDENT";
  accountObjective: "INCOME" | "LONG_TERM_GROWTH" | "SHORT_TERM_GROWTH";
  fundOrigin:
    | "ACCOUNTS_RECEIVABLE"
    | "RENTAL_INCOME"
    | "INCOME_FROM_PROFITS"
    | "MAINTENANCE"
    | "LEGAL_AGREEMENT"
    | "INSURANCE_INCOME"
    | "FAMILY"
    | "RETIREMENTS_SAVINGS"
    | "ROLL_OVER"
    | "BUSINESS_SALE"
    | "ACCUMULATED_SAVINGS"
    | "INVESTMENT_INCOME"
    | "GIFT"
    | "SALE_OF_REAL_ESTATE"
    | "LOTTERY_GAMES"
    | "OTHERS";
  timeHorizon: "ONE_OR_TWO_YEARS" | "TWO_TO_FIVE_YEARS" | "ABOVE_FIVE_YEARS";
  accountRiskProfile:
    | "CONSERVATIVE"
    | "MODERATE"
    | "AGGRESSIVE"
    | "SPECULATIVE";
  withdrawalReason:
    | "PERSONAL_EXPENSES"
    | "INVESTMENTS"
    | "SCHOOL_TUITION"
    | "OTHERS";
  withdrawalFrequency:
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUAL"
    | "ANNUAL"
    | "OTHER";
  transferReason:
    | "PERSONAL_EXPENSES"
    | "INVESTMENTS"
    | "SCHOOL_TUITION"
    | "OTHERS";
  transferFrequency:
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUAL"
    | "ANNUAL"
    | "OTHER";
  investmentObjective:
    | "INCOME"
    | "LONG_TERM_GROWTH"
    | "SHORT_TERM_GROWTH"
    | "INCOME_AND_APPRECIATION"
    | "SPECULATION"
    | "CAPITAL_PRESERVATION"
    | "HEDGE"
    | "OTHER";
}

export interface StepOneSecurities {
  "@type": string;
  referredByCommercialTeam: boolean;
  referrer?: string;
  taxNumber: string;
  accountHolderSize: number;
  holdersAreMarried?: boolean;
  personalInformation: {
    isAmericanCitizen: boolean;
    completeName: string;
    motherLastName?: string;
    gender: SecuritiesOptions["gender"];
    countryOfBirth?: string;
    birthDate: string; // Format: MM/dd/yyyy
    civilStatus: SecuritiesOptions["civilStatus"];
    cellPhone: string;
    landline?: string;
    workPhone?: string;
    email: string;
    educationLevel: SecuritiesOptions["educationLevel"];
    numberOfDependants: number;
    isSameAddress: boolean;
    taxDomicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
    postalDomicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  };
}

export interface StepTwoSecurities {
  "@type": string;
  residence: {
    isAmericanResident: boolean;
    fiscalResidenceCountry?: string;
    primaryCitizenship: string;
    hasAdditionalCitizenship: boolean;
    additionalCitizenship?: string;
  };
  identityInformation: {
    identificationType: SecuritiesOptions["documentType"];
    number: string;
    documentIssuingCountry: string;
    documentIssuingDate: string; // Format: MM/dd/yyyy
    documentExpirationDate: string; // Format: MM/dd/yyyy
  };
}

interface StepThreeWorkInformation {
  workSituation: SecuritiesOptions["employmentStatus"] | null;
  currentOccupation?: string;
  yearsInCurrentOccupation?: number;
  currentCompanyType?: string;
  previousOccupation?: string;
  yearsInPreviousOccupation?: number;
  employerInformation?: {
    name: string;
    address: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  };
}

// HACK: `workSituation` is on GET draft response, and `workInformation` is on POST step body
// both are the same, the name is misleading from the backend

export interface StepThreeSecurities {
  "@type": string;
  workInformation?: StepThreeWorkInformation;
  workSituation?: StepThreeWorkInformation;
  financialInformation: {
    annualIncomeFrom: number;
    annualIncomeTo: number;
    netIncomeFrom: number;
    netIncomeTo: number;
    liquidAssetsFrom: number;
    liquidAssetsTo: number;
  };
}

export interface StepFourSecurities {
  "@type": string;
  investmentKnowledge: {
    knowledgeLevel?: SecuritiesOptions["investmentKnowlageLevel"];
    instruments?: {
      [key in SecuritiesOptions["investmentTool"]]?: {
        knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"];
        since: number;
      };
    };
    haveAnotherInvestmentAccount: boolean;
    institutionName?: string;
    yearsOfExperienceInAccount?: number;
    familyAffiliation?: {
      brokerDealerName: string;
      employeeName: string;
      relationship: string;
    };
  };
}

export interface StepFiveSecurities {
  "@type": string;
  objectiveAndFunds: {
    objective: SecuritiesOptions["accountObjective"];
    accountOpeningObjective: string;
    originOfFunds: SecuritiesOptions["fundOrigin"];
    otherFundOrigin?: string;
    fundingPlan: number;
    initialTransferAmount: number;
    fundingSourceBankName: string;
    fundingSourceAccountHolder: string;
    timeHorizon: SecuritiesOptions["timeHorizon"];
  };
  riskProfile: SecuritiesOptions["accountRiskProfile"];
  transfersAndWithdrawals: {
    regularWithdrawals: boolean;
    withdrawalReason?: SecuritiesOptions["withdrawalReason"];
    otherWithdrawalReason?: string;
    thirdPartyTransfers: boolean;
    transferFrequency?: SecuritiesOptions["transferFrequency"];
    otherTransferFrequency?: string;
    thirdPartyTransfersReason?: SecuritiesOptions["transferReason"];
    otherThirdPartyTransfersReason?: string;
    withdrawalsFrequency?: SecuritiesOptions["withdrawalFrequency"];
    otherWithdrawalsFrequency?: string;
  };
  optionsAndMargins: {
    interestedInOptions: boolean;
    investmentObjectives?: SecuritiesOptions["investmentObjective"][];
    interestedInMargins: boolean;
  };
  interestedInAssigningBeneficiaries?: boolean;
  beneficiaries?: {
    name: string;
    assignedPercentage: number;
  }[];
}

export interface StepSixSecurities {
  "@type": string;
  beneficiariesInfo: {
    name: string;
    assignedPercentage: number;
    gender: SecuritiesOptions["gender"];
    birthDate: string; // Format: MM/dd/yyyy
    cellPhone: string;
    taxType: SecuritiesOptions["taxIdType"];
    taxNumber: string;
    relationshipWithBeneficiary: string;
    email: string;
    domicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  }[];
}

export interface HolderInformation {
  taxNumber: string /* String, required */;
  personalInformation: {
    /* PersonalInformationObsDTO, required */
    isAmericanCitizen: boolean /* Boolean, required */;
    completeName: string /* String, required */;
    motherLastName?: string /* String, optional */;
    gender: SecuritiesOptions["gender"] /* Gender, required (Values: MALE, FEMALE, OTHER) */;
    countryOfBirth?: string /* String, optional */;
    birthDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
    civilStatus: SecuritiesOptions["civilStatus"] /* CivilStatus, required (Values: SINGLE, MARRIED, DIVORCED, WIDOWED) */;
    cellPhone: string /* String, required (Valid Telephone format) */;
    landline?: string /* String, optional */;
    workPhone?: string /* String, optional */;
    email: string;
    educationLevel: SecuritiesOptions["educationLevel"] /* EducationLevel, required (Values: NONE, PRIMARY, SECONDARY, TERTIARY, UNIVERSITY, POSTGRADUATE, MASTER, DOCTORATE) */;
    numberOfDependants: number /* Integer, required */;
    isSameAddress: boolean /* Boolean, required */;
    taxDomicile: {
      /* required */ address: string /* String, required */;
      postalCode: string /* String, required */;
      city: string /* String, required */;
      province: string /* String, required */;
      country: string /* String, required */;
      floor?: string | null;
      apartment?: string | null;
    };
    postalDomicile: {
      /* required */ address: string /* String, required */;
      postalCode: string /* String, required */;
      city: string /* String, required */;
      province: string /* String, required */;
      country: string /* String, required */;
      floor?: string | null;
      apartment?: string | null;
    };
  };
  residence: {
    /* required */ isAmericanResident: boolean /* Boolean, required */;
    fiscalResidenceCountry?: string /* String, optional */;
    primaryCitizenship: string /* String, required */;
    hasAdditionalCitizenship: boolean /* Boolean, required */;
    additionalCitizenship?: string /* String, optional */;
  };
  identityInformation: {
    /* required */
    identificationType: SecuritiesOptions["documentType"] /* IdentificationType, required (Values: DNI, LE, LC, PASSPORT, CI) */;
    number: string /* String, required */;
    documentIssuingCountry: string /* String, required */;
    documentIssuingDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
    documentExpirationDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
  };
  workInformation: {
    /*required */
    workSituation: SecuritiesOptions["employmentStatus"] | null /* WorkSituation, required (Values: EMPLOYEE, INDEPENDENT, RETIRED, UNEMPLOYED, STUDENT) */;
    currentOccupation?: string /* String, optional */;
    yearsInCurrentOccupation?: number /* Integer, optional */;
    currentCompanyType?: string /* String, optional */;
    previousOccupation?: string /* String, optional */;
    yearsInPreviousOccupation?: number /* Integer, optional */;
    employerInformation?: {
      /* optional */ name: string /* String, required */;
      address: {
        /* AddressObsDTO, required */ address: string /* String, required */;
        postalCode: string /* String, required */;
        city: string /* String, required */;
        province: string /* String, required */;
        country: string /* String, required */;
        floor?: string | null;
        apartment?: string | null;
      };
    };
  };
  financialInformation: {
    annualIncomeFrom: number;
    annualIncomeTo: number;
    netIncomeFrom: number;
    netIncomeTo: number;
    liquidAssetsFrom: number;
    liquidAssetsTo: number;
  };
  investmentKnowledge: {
    /* required */
    knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"] /* KnowledgeLevel, required (Values: LIMITED, MODERATED, EXTENSIVE, NONE) */;
    instruments?: {
      [key in SecuritiesOptions["investmentTool"]]: {
        knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"];
        since: number;
      };
    };
    haveAnotherInvestmentAccount: boolean /* Boolean, required */;
    institutionName?: string /* String, optional */;
    yearsOfExperienceInAccount?: number /* Integer, optional */;
    familyAffiliation?: {
      brokerDealerName: string;
      employeeName: string;
      relationship: string;
    };
  };
}

export interface StepSevenSecurities {
  "@type": string;
  holders?: HolderInformation[] /* ArrayList<HolderInformationDTO>, optional */;
}

// Securities steps form types

export const BENEFICIARIES_LIMIT = 5;
// Create a utility type for beneficiary fields
export type BeneficiarySetupFields<N extends number> = {
  [K in `beneficiary${number extends N ? number : N}`]: {
    name: string;
  };
};
// Create a type that generates N beneficiary fields
export type GenerateBeneficiarySetupFields<N extends number> = {
  [K in keyof BeneficiarySetupFields<N>]?: BeneficiarySetupFields<N>[K];
} & {
  [K in `beneficiary${number extends N ? number : N}Percentage`]?: string;
};

// Create a utility type for detailed beneficiary fields
export type BeneficiaryFields<N extends number> = {
  [K in `beneficiary${number extends N ? number : N}`]: {
    name: string;
    gender: SecuritiesOptions["gender"];
    birthDate: string; // Format: DD/MM/YYYY
    email: string;
    phoneAR?: string;
    phoneUS?: string;
    taxIdType?: SecuritiesOptions["taxIdType"];
    taxIdNumber?: string;
    socialSecurityNumber?: string;
    relationship: string;
    country: string;
    fullAddress: string;
    city: string;
    zipCode: string;
    state: string;
    floor?: string;
    apartment?: string;
    documentFront: SerializableFile | null;
    documentBack?: SerializableFile | null;
  };
};

// Create a type that generates N detailed beneficiary fields
export type GenerateBeneficiaryFields<N extends number> = {
  [K in keyof BeneficiaryFields<N>]?: BeneficiaryFields<N>[K];
} & {
  [K in `beneficiary${number extends N ? number : N}Percentage`]?: string;
};

export interface SecuritiesFormikValues {
  prevStep: {
    email: string;
    taxId?: string;
    socialSecurityNumber?: string;
    referred?: string;
  };
  stepOne: {
    name: string;
    lastName: string;
    motherName?: string;
    gender: SecuritiesOptions["gender"];
    countryOfBirth: string;
    birthDate: string; // Format: DD/MM/YYYY
    civilStatus: SecuritiesOptions["civilStatus"];
    educationLevel: SecuritiesOptions["educationLevel"];
    dependants: string; // 0-11 string
    mobilePhoneAR?: string;
    mobilePhoneUS?: string;
    homePhoneAR?: string;
    homePhoneUS?: string;
    workPhoneAR?: string;
    workPhoneUS?: string;
    addressesSameAsFirstHolders: boolean;
    email: string;
    taxAddresses: {
      country: string;
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    postalAddressDifferent: boolean;
    postalAddresses?: {
      country: string;
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    proofOfAddress: SerializableFile | null;
  };
  stepTwo: {
    USResident: string; // SI or NO
    residenceCountry: string;
    firstCitizenship: string;
    additionalCitizenshipBool: string; // SI or NO
    additionalCitizenship?: string;
    socialSecurityNumber?: string;
    documentTypeAR?: SecuritiesOptions["documentType"];
    documentTypeUS?: SecuritiesOptions["documentType"];
    documentNumber?: string;
    passportNumber?: string;
    documentCountry: string;
    documentEmisionDate: string; // Format: DD/MM/YYYY
    documentExpirationDate: string; // Format: DD/MM/YYYY
    documentFrontDNI?: SerializableFile;
    documentBackDNI?: SerializableFile;
    documentFrontPASSPORT?: SerializableFile;
    documentBackPASSPORT?: SerializableFile;
    taxId?: string;
  };
  stepThree: {
    employmentStatus: SecuritiesOptions["employmentStatus"];
    occupation?: string;
    yearsWorked?: string; // number string
    companyType?: string;
    previousOccupation?: string;
    yearsPreviouslyWorked?: string; // number string
    companyName?: string;
    companyCountry?: string;
    companyAddresses?: {
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    annualIncomeLowerBound: string; // number string
    annualIncomeUpperBound: string; // number string
    netIncomeLowerBound: string; // number string
    netIncomeUpperBound: string; // number string
    netWorthLowerBound: string; // number string
    netWorthUpperBound: string; // number string
  };
  stepFour: {
    investmentsExperience: SecuritiesOptions["investmentKnowlageLevel"];
    investmentTools: {
      [key in SecuritiesOptions["investmentToolsKeys"]]: {
        bool: boolean;
        knowlage?: SecuritiesOptions["investmentKnowlageLevel"];
        years?: string; // number string
      };
    };
    hasOtherInvestmentAccountsBool: "SI" | "NO";
    otherInvestmentAccountName?: string;
    otherInvestmentAccountYears?: string; // number string
    isAffiliatedWithBroker: string; // SI or NO
    brokerName?: string;
    brokerEmployeeName?: string;
    brokerEmployeeRelationship?: string;
  };
  stepFiveFirstSubStep: {
    accountObjective: SecuritiesOptions["accountObjective"];
    accountOpeningObjective: string;
    originOfFunds: SecuritiesOptions["fundOrigin"];
    otherOriginOfFunds?: string;
    fundingDate: string; // number string, it represents the number of days
    initialTransferAmount: string; // number string
    fundingSourceBankName: string;
    fundingSourceAccountHolder: string;
    timeHorizon: SecuritiesOptions["timeHorizon"];
    riskProfile: SecuritiesOptions["accountRiskProfile"];
    regularWithdrawals: {
      bool: "SI" | "NO";
      reason?: SecuritiesOptions["withdrawalReason"];
      otherReason?: string;
      frequency?: SecuritiesOptions["withdrawalFrequency"];
      otherFrequency?: string;
    };
    thirdPartyTransfers: {
      bool: "SI" | "NO";
      reason?: SecuritiesOptions["transferReason"];
      otherReason?: string;
      frequency?: SecuritiesOptions["transferFrequency"];
      otherFrequency?: string;
    };
    tradeOptions: {
      bool: "SI" | "NO";
      options?: SecuritiesOptions["investmentObjective"][];
    };
    tradeMargins: {
      bool: "SI" | "NO";
    };
    beneficiariesBool: "SI" | "NO";
  } & GenerateBeneficiarySetupFields<1 | 2 | 3 | 4 | 5>;
  stepFiveSecondSubStep: GenerateBeneficiaryFields<1 | 2 | 3 | 4 | 5>;
}

export type PartnerCompanyType =
  "LANDTOKEN" | "LA_BRAGADENSE" | "SAV3" | "INTERVALORES";

export type CompanyPersonType =
  | "ALLARIA_INDIVIDUAL"
  | "ALLARIA_BUSINESS"
  | "ALLARIAMAS_INDIVIDUAL"
  | "ALLARIAMAS_BUSINESS"
  | "AGROFINANZAS_INDIVIDUAL"
  | "AGROFINANZAS_BUSINESS"
  | "ALLARIA_SECURITY_INDIVIDUAL"
  | "ALLARIA_SECURITY_BUSINESS";

export type CreateCryptoOnboardingRequest = {
  accountId: number;
  pep: string;
  pepDetails?: string;
  cryptoExperience: string;
  phone: string;
  termsAccepted: boolean;
};
